import moment from 'moment';
import { TimeZoneHelper } from './timeZoneHelper';

export class MeetingTimeZoneHelper {
    /**
     * The short name of the zone the viewer's browser shows `date` in, e.g. 'CDT'
     * (outside the US the browser gives an offset such as 'GMT+3').
     */
    public static getViewerTimeZoneLabel(date: Date | string): string {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZoneName: 'short',
        }).formatToParts(new Date(date));
        return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
    }

    /**
     * The viewer's zone at `date` to record with a meeting picked in the viewer's local time, e.g. 'CDT'.
     * Undefined if the browser names the zone longer than the 10 characters the API stores.
     */
    public static getViewerTimeZoneToRecord(date: Date | string): string | undefined {
        const label = this.getViewerTimeZoneLabel(date);
        return label && label.length <= 10 ? label : undefined;
    }

    /** A meeting time in the viewer's zone with that zone named, e.g. '10:00 AM CDT'. */
    public static formatTime(date: Date | string): string {
        return `${moment(date).format('h:mm A')} ${this.getViewerTimeZoneLabel(
            date
        )}`.trim();
    }

    /** A time range in the viewer's zone with the zone named once, e.g. '8:00 PM - 9:00 PM EDT'. */
    public static formatTimeRange(
        dateStart: Date | string,
        dateEnd: Date | string
    ): string {
        return `${moment(dateStart).format('h:mm A')} - ${this.formatTime(
            dateEnd
        )}`;
    }

    /**
     * The time a meeting was scheduled for, in the zone it was scheduled in,
     * e.g. 'Scheduled for 10/06/2026 10:00 AM – 11:00 AM CDT'. Undefined when no zone was
     * recorded or the recorded zone is unknown.
     */
    public static getScheduledTimeNote(
        dateStart: Date | string,
        dateEnd: Date | string,
        scheduledTimeZone?: string | null
    ): string | undefined {
        if (!scheduledTimeZone || !dateStart || !dateEnd) {
            return undefined;
        }

        const offsetHours = this.getOffsetHours(scheduledTimeZone);
        if (offsetHours === undefined) {
            return undefined;
        }

        const zoneStart = moment.utc(new Date(dateStart)).add(offsetHours, 'hours');
        const zoneEnd = moment.utc(new Date(dateEnd)).add(offsetHours, 'hours');
        const endFormat = zoneStart.isSame(zoneEnd, 'day')
            ? 'h:mm A'
            : 'MM/DD/YYYY h:mm A';

        return `Scheduled for ${zoneStart.format(
            'MM/DD/YYYY h:mm A'
        )} – ${zoneEnd.format(endFormat)} ${scheduledTimeZone}`;
    }

    /**
     * The UTC offset of a recorded zone: a US abbreviation ('CDT'), or the offset form
     * browsers outside the US name their zone with ('GMT+3', 'GMT-3:30', 'GMT', 'UTC').
     */
    private static getOffsetHours(zone: string): number | undefined {
        const usZone = TimeZoneHelper.getTimeZoneByAbbreviation(zone);
        if (usZone) {
            return usZone.offset;
        }

        const match = /^(?:GMT|UTC)(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/.exec(zone);
        if (!match) {
            return undefined;
        }
        if (!match[1]) {
            return 0;
        }

        const hours = Number(match[2]) + Number(match[3] ?? 0) / 60;
        return match[1] === '-' ? -hours : hours;
    }
}
